import BackIcon from '../../assets/icons/back.svg?react';
import CurrencyRubIcon from '../../assets/icons/currency-rub.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { OrderApi, ReceiptApi } from '../../api';
import {
  formatDateRu,
  formatDaysPlural,
  formatPerDose,
  getOrderStatusConfig,
} from '../../utils';
import { OrderDetailsSkeleton } from '../molecules';

type Props = {
  orderId: number;
  onBack?: () => void;
};

const formatPrice = (price: number) =>
  price.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const ClientOrderDetails = ({ orderId, onBack }: Props) => {
  const navigate = useNavigate();
  const { data: orderDetails } = useQuery({
    queryKey: [OrderApi.KEY, 'getById', orderId],
    queryFn: () => OrderApi.getById(orderId, true),
    enabled: orderId !== undefined,
  });

  // Комментарий, который клиент написал при оформлении, лежит в рецепте
  // (в заказе last_comment — это переписка аптеки, её заполняют позже).
  const receiptId = orderDetails?.receipt_id;
  const { data: receipt } = useQuery({
    queryKey: [ReceiptApi.KEY, 'getById', receiptId],
    queryFn: () => ReceiptApi.getById(receiptId as number),
    enabled: !!receiptId,
  });

  if (!orderDetails) {
    // Заглушка по форме карточки — раньше при загрузке был пустой экран.
    return <OrderDetailsSkeleton />;
  }

  const isPaid =
    orderDetails.status?.code_integer === 3 ||
    orderDetails.status?.code === 'PAID';
  const orderStatus = getOrderStatusConfig(orderDetails.status?.value ?? '');

  const orderDate = orderDetails.create_date
    ? formatDateRu(orderDetails.create_date, 'd MMMM yyyy, HH:mm')
    : null;

  const formatPickupDate = () => {
    if (!orderDetails.delivery_date) return '—';
    return formatDateRu(orderDetails.delivery_date, 'd MMMM yyyy, HH:mm');
  };

  const formatMedicineInfo = (item: (typeof orderDetails.items)[0]) => {
    const { medicine } = item;
    const dosage = medicine?.dosage ?? '';
    const dosageType = medicine?.dosage_type?.value ?? '';
    const tablets = item.total_tablets ?? item.totalAmount ?? item.amount;
    return `${dosage} ${dosageType}, ${tablets}шт`;
  };

  const formatDaytime = (item: (typeof orderDetails.items)[0]) => {
    const receiptMedicine = (
      item as {
        receipt_medicine?: Array<{ daytime?: { value?: string } | null }>;
      }
    ).receipt_medicine;
    const first = Array.isArray(receiptMedicine)
      ? receiptMedicine[0]
      : receiptMedicine;
    const time = first?.daytime?.value;
    if (!time) return null;
    return time.includes(':') ? `Прием в ${time}` : `Прием в ${time}`;
  };

  /** Полная фраза приёма: «Принимать по 2 капсулы 1 раз в день утром до еды в течение 5 дней.» */
  const formatWhenToTake = (item: (typeof orderDetails.items)[0]) => {
    const raw = item as {
      receipt_medicine?: Array<{
        amount?: number;
        duration?: number;
        frequency?: { value?: string };
        daytime?: { value?: string } | null;
        eating?: { value?: string };
      }>;
      medicine?: { form_type?: { value?: string } };
    };
    const list = Array.isArray(raw.receipt_medicine)
      ? raw.receipt_medicine
      : raw.receipt_medicine
      ? [raw.receipt_medicine]
      : [];
    if (list.length === 0) return null;
    const formType = raw.medicine?.form_type?.value ?? '';
    const daytimes: Record<string, string> = {
      Утро: 'утром',
      День: 'днём',
      Вечер: 'вечером',
      Ночь: 'ночью',
    };
    const sentences = list
      .map((rm) => {
        const amount = rm?.amount ?? 1;
        const duration = rm?.duration;
        const frequency = rm?.frequency?.value;
        const daytime = rm?.daytime?.value;
        const eating = rm?.eating?.value;
        const parts: string[] = [];
        parts.push(`Принимать ${formatPerDose(amount, formType)}`);
        parts.push(frequency === 'В день' ? '1 раз в день' : frequency ?? '');
        if (daytime) parts.push(daytimes[daytime] ?? daytime.toLowerCase());
        if (eating) parts.push(eating.toLowerCase());
        if (duration != null) {
          parts.push(`в течение ${duration} ${formatDaysPlural(duration)}`);
        }
        return parts.filter(Boolean).join(' · ') + '.';
      })
      .filter(Boolean);
    return sentences.length > 0 ? sentences.join(' ') : null;
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const paymentLink = orderDetails.payment_link;

  return (
    // Ширина карточки по макету — 968px. Ограничение нужно именно здесь:
    // внешний блок белый, и без него фон с полосой «Вернуться назад» уезжал
    // на всю ширину страницы, за правый край содержимого.
    <div className="w-full max-w-[968px] overflow-hidden rounded-[16px] bg-white">
      {/* Полоса «Вернуться назад» — как на референсе и в кабинете врача */}
      <div className="flex h-[74px] w-full items-center bg-[#f1f7fa] px-[24px]">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-[4px] font-['Ubuntu'] text-[20px] font-bold leading-[30px] text-[#151515] hover:opacity-80"
        >
          <BackIcon className="size-[24px] shrink-0" />
          <span>Вернуться назад</span>
        </button>
      </div>

      {/* rounded-b: сверху блок примыкает к полосе «Вернуться назад», и
          скругление прорезало плашку статуса — в углах проступал белый фон. */}
      <div className="w-full overflow-hidden rounded-b-[16px] bg-white">
        {/* Плашка оплаты */}
        <div
          className={`flex flex-col items-start ${
            isPaid ? 'bg-[#5fd98e]/25' : 'bg-[#f93e3e]/10'
          }`}
        >
          <div className="flex w-full items-center justify-between rounded-t-[16px] px-[24px] py-[12px]">
            <span
              className={`font-['Ubuntu'] text-base font-medium leading-5 ${
                isPaid ? 'text-[#00bf49]' : 'text-[#f83d3d]'
              }`}
            >
              {isPaid ? 'Заказ оплачен' : 'Заказ не оплачен'}
            </span>
            {!isPaid && paymentLink && (
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-center font-['Ubuntu'] text-base font-normal leading-5 text-[#151515] underline"
              >
                Оплатить заказ
              </a>
            )}
          </div>

          {/* Номер, статус, дата/адрес (оплаченный: только дата и адрес; неоплаченный: + комментарий) */}
          <div className="flex w-full flex-col items-start gap-[16px] rounded-t-[16px] bg-white p-[24px]">
            <div className="flex min-h-[40px] w-full items-center">
              <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px]">
                <span className="font-['Ubuntu'] text-2xl font-bold leading-9 text-[#151515]">
                  №{orderDetails.id}
                </span>
                <div
                  className={`flex items-center justify-center gap-[10px] rounded-[100px] px-[12px] py-[4px] ${orderStatus.bg}`}
                >
                  <span
                    className={`font-['Ubuntu'] text-sm font-medium leading-5 ${orderStatus.text}`}
                  >
                    {orderStatus.label}
                  </span>
                </div>
                {orderDate && (
                  <span className="font-['Ubuntu'] text-sm font-normal leading-5 text-[#151515]/50">
                    от {orderDate}
                  </span>
                )}
              </div>
            </div>

            <div className="inline-flex w-full flex-wrap items-start justify-start gap-[12px]">
              {/*<div className="min-w-[0px] flex-1 rounded-[12px] bg-[#f7f8f9] p-[16px]">*/}
              {/*  <div className="font-['Ubuntu'] text-sm font-normal leading-5 text-[#151515]/50">*/}
              {/*    Дата и время самовывоза*/}
              {/*  </div>*/}
              {/*  <div className="font-['Ubuntu'] text-base font-bold leading-6 text-[#151515]">*/}
              {/*    {formatPickupDate()}*/}
              {/*  </div>*/}
              {/*</div>*/}
              {/*<div className="min-w-[0px] flex-1 rounded-[12px] bg-[#f7f8f9] p-[16px]">*/}
              {/*  <div className="font-['Ubuntu'] text-sm font-normal leading-5 text-[#151515]/50">*/}
              {/*    Адрес самовывоза*/}
              {/*  </div>*/}
              {/*  <div className="font-['Ubuntu'] text-base font-bold leading-6 text-[#151515]">*/}
              {/*    {orderDetails.delivery_address?.address_text || '—'}*/}
              {/*  </div>*/}
              {/*</div>*/}
              {!isPaid && (
                <div className="w-full min-w-0 rounded-[12px] bg-[#f7f8f9] p-[16px]">
                  <div className="font-['Ubuntu'] text-sm font-normal leading-5 text-[#151515]/50">
                    Комментарий
                  </div>
                  <div className="font-['Ubuntu'] text-base font-bold leading-6 text-[#151515]">
                    {orderDetails.last_comment ||
                      receipt?.description?.trim() ||
                      '—'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Блок лекарств */}
        <div className="flex flex-col items-start rounded-[16px] bg-[#e4f0f7]">
          <div className="flex min-h-[52px] w-full flex-wrap items-center justify-between gap-y-2 rounded-t-[16px] px-[24px] py-[16px]">
            <div className="flex items-center gap-[12px]">
              <span className="font-['Ubuntu'] text-base font-medium leading-5 text-[#151515]">
                Лекарства
              </span>
            </div>
            <div className="flex min-w-0 shrink items-center gap-[6px]">
              <span className="shrink-0 whitespace-nowrap font-['Ubuntu'] text-base font-normal leading-5 text-[#151515]">
                Сформировал(а):
              </span>
              <span className="min-w-0 truncate font-['Ubuntu'] text-base font-normal leading-5 text-[#0788f1] underline">
                {orderDetails.doctor?.surnameAndInitials || 'Вы'}
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-[10px] rounded-t-[16px] bg-white p-[24px]">
            {!orderDetails.items || orderDetails.items.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center gap-[8px] py-[24px]">
                <div className="w-full text-center font-['Ubuntu'] text-[20px] font-medium leading-[28px] text-[#151515]">
                  Заказ формируется
                </div>
                <div className="flex w-full max-w-[560px] flex-col items-center gap-[8px] text-center">
                  <span className="font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-70">
                    Фармацевт сейчас подбирает препараты и рассчитывает
                    стоимость.
                  </span>
                  <span className="font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-70">
                    Цены появятся в ближайшее время — мы уведомим вас, как
                    только заказ будет готов к оплате.
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-start gap-[7px]">
                {orderDetails.items.map((item, index) => {
                  const whenToTake = formatWhenToTake(item);
                  const daytime = formatDaytime(item);
                  return (
                    <div key={item.id ?? index} className="w-full">
                      <div className="flex w-full items-start gap-[12px] py-[12px]">
                        <div className="flex w-[20px] flex-col items-center justify-center gap-[10px] pt-[2px]">
                          <span className="font-['Ubuntu'] text-[16px] font-normal leading-[20px] text-[#151515] opacity-30">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex flex-1 items-start gap-[16px]">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-start gap-[12px]">
                              <span className="font-['Ubuntu'] text-[16px] font-bold leading-[24px] text-[#151515]">
                                {item.medicine.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index < orderDetails.items.length - 1 && (
                        <div className="h-[0.5px] w-full bg-[#151515] opacity-10" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {orderDetails.items?.length > 0 && (
              <>
                <div className="h-[0.5px] w-full bg-[#151515] opacity-10" />
                {/* Итого и разбивка (API: price, delivery_price, package_fee, vat_amount, total_price) */}
                <div className="mt-[24px] flex w-full flex-col gap-[4px]">
                  <div className="flex h-[24px] w-full items-center justify-between">
                    <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                      Итого
                    </span>
                    <div className="flex items-center gap-[2px]">
                      <span className="font-['Ubuntu'] text-xl font-medium leading-6 text-[#151515]">
                        {formatPrice(
                          orderDetails.total_price ?? orderDetails.price ?? 0,
                        )}
                      </span>
                      <CurrencyRubIcon className="size-[20px]" />
                    </div>
                  </div>
                  <div className="flex h-[24px] w-full items-center justify-between">
                    <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                      Стоимость препаратов
                    </span>
                    <div className="flex items-center gap-[2px]">
                      <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                        {formatPrice(orderDetails.price ?? 0)}
                      </span>
                      <CurrencyRubIcon className="size-[14px]" />
                    </div>
                  </div>
                  <div className="flex h-[24px] w-full items-center justify-between">
                    <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                      Доставка
                    </span>
                    <div className="flex items-center gap-[2px]">
                      <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                        {formatPrice(orderDetails.delivery_price ?? 0)}
                      </span>
                      <CurrencyRubIcon className="size-[14px]" />
                    </div>
                  </div>
                  <div className="flex h-[24px] w-full items-center justify-between">
                    <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                      Сборка заказа
                    </span>
                    <div className="flex items-center gap-[2px]">
                      <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                        {formatPrice(orderDetails.package_fee ?? 0)}
                      </span>
                      <CurrencyRubIcon className="size-[14px]" />
                    </div>
                  </div>
                  <div className="flex h-[24px] w-full items-center justify-between">
                    <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                      В том числе НДС
                    </span>
                    <div className="flex items-center gap-[2px]">
                      <span className="font-['Ubuntu'] text-base font-normal leading-6 text-[#151515]">
                        {formatPrice(orderDetails.vat_amount ?? 0)}
                      </span>
                      <CurrencyRubIcon className="size-[14px]" />
                    </div>
                  </div>
                </div>

                {/* Кнопка оплаты внизу */}
                {!isPaid && paymentLink && (
                  <a
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-[16px] flex w-full items-center justify-center gap-[12px] self-stretch rounded-[12px] bg-[#0788f1] px-[16px] py-[10px]"
                  >
                    <span className="flex-1 text-center font-['Ubuntu'] text-base font-normal leading-6 text-white">
                      Оплатить заказ
                    </span>
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
